/**
 * Headless target for the bulk report worker (spec §4.3). Not linked from the
 * app. A server-side Chromium tab signs in as the sender, opens this page and
 * calls window.__sendReport for one bill at a time; the page renders
 * BillReportScreen and captures it exactly as the dialog did.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { BillReportScreen } from '../components/bills/BillReportScreen';
import { renderReportHtmlBlob, renderReportPdfBlob } from '../utils/pdfUtils';
import { reportFigures, NO_SENDS } from '../lib/reportStatus';
import { sendOneBillReport } from '../lib/sendOneBillReport';
import type { SendOneInput, SendOneResult } from '../lib/reportBatchTypes';
import { bvReportService, type BvEmailTemplate } from '../services/bvReportService';
import { bulkReportService } from '../services/bulkReportService';
import type { BvBill } from '../types/billTypes';

declare global {
  interface Window {
    __reportPrintReady?: boolean;
    __sendReport?: (input: SendOneInput) => Promise<SendOneResult>;
  }
}

/** Two frames: React has committed the report and the browser has laid it out. */
const nextPaint = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

export const ReportPrintPage = () => {
  const [renderBill, setRenderBill] = useState<BvBill | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const templatesRef = useRef<BvEmailTemplate[] | null>(null);

  useEffect(() => {
    // The worker opens this page with Vercel's one-time bypass params; the
    // cookie is already set, so don't leave the secret in location/referrers.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('x-vercel-protection-bypass') || url.searchParams.has('x-vercel-set-bypass-cookie')) {
        url.searchParams.delete('x-vercel-protection-bypass');
        url.searchParams.delete('x-vercel-set-bypass-cookie');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      // Cosmetic only — never block the page.
    }

    // The worker's tabs share one session; a refresh from here would rotate the
    // refresh token under the other tabs. The server re-mints it instead.
    void supabase.auth.stopAutoRefresh();

    const render = async (bill: BvBill): Promise<HTMLElement> => {
      setRenderBill(bill);
      await nextPaint();
      // Never capture the previous bill's report.
      const rendered = () => hostRef.current?.getAttribute('data-render-bill') === bill.id;
      if (!rendered()) await nextPaint();
      if (!rendered() || !hostRef.current) throw new Error('The report did not render in time');
      return (hostRef.current.querySelector('[data-pdf-target]') ?? hostRef.current) as HTMLElement;
    };

    const templateFor = async (templateId: string | null): Promise<BvEmailTemplate | null> => {
      if (!templatesRef.current) {
        const res = await bvReportService.listEmailTemplates();
        if (!res.success) throw new Error(res.error ?? 'Could not load the email templates');
        templatesRef.current = res.data ?? [];
      }
      return templateId ? templatesRef.current.find((t) => t.id === templateId) ?? null : null;
    };

    window.__sendReport = async (input) => {
      let template: BvEmailTemplate | null;
      try {
        template = await templateFor(input.templateId);
      } catch (err) {
        return { state: 'failed', error: err instanceof Error ? err.message : 'Could not load the email templates' };
      }
      return sendOneBillReport(input, {
        template,
        loadBill: async (billId) => {
          const res = await bulkReportService.getBillsForSend([billId]);
          if (!res.success || !res.data) return { success: false, error: res.error ?? 'Could not re-read this bill' };
          return { success: true, data: { bill: res.data.bills[0] ?? null, history: res.data.history[billId] ?? NO_SENDS } };
        },
        loadContext: (billId) => bvReportService.context(billId),
        render,
        capture: (target, format, subject) => (format === 'html' ? renderReportHtmlBlob(target, subject) : renderReportPdfBlob(target)),
        send: (payload) => bvReportService.send(payload),
      });
    };
    window.__reportPrintReady = true;

    return () => {
      window.__reportPrintReady = false;
      delete window.__sendReport;
    };
  }, []);

  const figures = renderBill ? reportFigures(renderBill) : null;
  return (
    <div className="p-4">
      <p className="text-xs text-gray-400">Bill validation report sender</p>
      {renderBill && figures && (
        <div style={{ width: 900 }}>
          <div ref={hostRef} key={renderBill.id} data-render-bill={renderBill.id}>
            <BillReportScreen
              bill={renderBill}
              shadowResult={figures.shadowResult}
              actualResult={figures.actualResult}
              gstExcluded={figures.gstExcluded}
              retailer={renderBill.site?.current_retailer ?? undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};
